import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { combineLatest, Observable ,  of, Subscription } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { DateTimeChartDataModel } from '../../models/date-time-chart-data.model';
import { timeScalesEnum } from '../../enums/timeScales.enum';
import { DateTimeChartDataItemModel } from '../../models/date-time-chart-data-item.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { DateRange } from '../../models/date-time-chart-data-item.model';
@Component({
    selector: 'agpr-coverage-over-time',
    templateUrl: './coverage-over-time.component.html',
    styleUrls: ['./coverage-over-time.component.scss'],
    standalone: false
})
export class CoverageOverTimeComponent implements AfterViewInit, OnDestroy {

    @ViewChild('chart', { static: true }) chartElement: ElementRef;

    @Input() data: Observable<DateTimeChartDataModel>;

    @Output() selectedDataItems: EventEmitter<DateRange>;

    private chart: am4charts.XYChart;
    private averageRange: am4charts.ValueAxisDataItem;
    private dateAxis: am4charts.DateAxis<am4charts.AxisRenderer>;
    private scale: timeScalesEnum;
    private bullet: am4charts.CircleBullet;
    public loading: boolean;

    subscriptions: Subscription[] = [];

    constructor(
        private zone: NgZone,
        private currentUserStateService: CurrentUserStateService,
        private cdr: ChangeDetectorRef,
        private userPreferencesService: UserPreferencesService
    ) {
        this.selectedDataItems = new EventEmitter<DateRange>();
        this.loading = true;
    }

    private calculateAverage(data: Array<any>, field: string): number {
        let sum = 0;
        const length = data.length;

        if (length === 0) {
            return 0;
        }

        for (let i = 0; i < length; i++) {
            sum += data[i][field];
        }

        return sum / length;
    }

    /**
     * Initialize the chart.
     * NOTE: This must be run outside of Angular zone to work properly.
     */
    private initChart(chartColor: string): am4charts.XYChart {
        am4core.options.commercialLicense = true;
        const chart = am4core.create(this.chartElement.nativeElement, am4charts.XYChart);

        // Create axes
        this.dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        this.dateAxis.renderer.grid.template.location = 0;
        this.dateAxis.renderer.minGridDistance = 50;

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.minGridDistance = 20;
        // Never show values lower than 0
        valueAxis.min = 0;
        // Make the top on the axis 10% higher than the highest actual value
        valueAxis.extraMax = 0.1;
        // Only show integer values
        valueAxis.maxPrecision = 0;
        this.averageRange = valueAxis.axisRanges.create();

        // Create series
        const series: am4charts.LineSeries = chart.series.push(new am4charts.LineSeries());
        series.stroke = am4core.color(chartColor);
        const dataFields: am4charts.IXYSeriesDataFields = series.dataFields;
        dataFields.valueY = 'value';
        dataFields.dateX = 'dateTime';
        series.tooltipText = '{dateRange.tooltip} - {value}';
        series.strokeWidth = 3;
        // Makes the line curved
        series.tensionX = 0.8;

        this.bullet = series.bullets.push(new am4charts.CircleBullet());
        this.bullet.circle.strokeWidth = 2;
        this.bullet.circle.radius = 2;
        this.bullet.circle.fill = am4core.color('#fff');

        this.bullet.events.on('hit', (ev) => {
            if (ev &&
                ev.target &&
                ev.target.className === 'CircleBullet' &&
                ev.target.dataItem
            ) {
                // Cast to avoid typescript complaining about types
                const lineSeriesDataItem: am4charts.LineSeriesDataItem = ev.target.dataItem as am4charts.LineSeriesDataItem;

                const dataPoint: DateTimeChartDataItemModel = this.chart.data.find(x => {
                    return (moment(x.dateTime).isSame(lineSeriesDataItem.dateX) && x.value === lineSeriesDataItem.valueY);
                });

                // Emit the event via angular so other things can respond to it.
                // If it's left outside of angular then anything in angular that is subscribed will be ignored
                this.zone.run(
                    () => {
                        this.selectedDataItems.emit(dataPoint?.dateRange);
                    }
                );
            }
        });

        const bullethover = this.bullet.states.create('hover');
        bullethover.properties.scale = 1.3;

        // Make a cursor
        chart.cursor = new am4charts.XYCursor();
        chart.cursor.xAxis = this.dateAxis;
        this.dateAxis.cursorTooltipEnabled = false;
        valueAxis.cursorTooltipEnabled = false;

        // Add scrollbar
        chart.scrollbarX = new am4core.Scrollbar();
        chart.scrollbarX.exportable = false;

        return chart;
    }

    private setScale(scale: timeScalesEnum) {
        this.scale = scale;
        switch (scale) {
            case timeScalesEnum.daily:
                this.dateAxis.baseInterval = {
                    'timeUnit': 'day',
                    'count': 1
                };

                this.chart.dateFormatter.dateFormat = 'MMM d';
                this.bullet.cursorOverStyle = am4core.MouseCursorStyle.pointer;
                break;
            case timeScalesEnum.hourly:
            default:
                this.dateAxis.baseInterval = {
                    'timeUnit': 'hour',
                    'count': 1
                };

                this.chart.dateFormatter.dateFormat = 'HH:mm';
                this.bullet.cursorOverStyle = am4core.MouseCursorStyle.default;
                break;
        }
    }

    exportData(type: keyof am4core.IExportOptions): void {
        this.chart.exporting.filePrefix = 'coverageOverTime_' + moment().format('YYYY-MM-DD');
        const exportData = this.chart.data.slice();
        exportData.forEach(data => {
            data['fromTime'] = moment(data.dateRange.from).format('YYYY/MM/DD hh:mm A');
            data['toTime'] = moment(data.dateRange.to).format('YYYY/MM/DD hh:mm A');
        });
        const dataFields = {
            dateTime: 'dateTime',
            value: 'value',
            fromTime:'fromTime',
            toTime: 'toTime'
        };
        // turn it off, otherwise, amcharts will call all fontawesome fonts apis and some of them have CORS error
        this.chart.exporting.useWebFonts = false;
        this.chart.exporting.dataFields = dataFields;
        // .export returns a Promise<boolean> but there's not much that can be done with it currently
        this.chart.exporting.export(type);
    }

    ngAfterViewInit() {
        this.subscriptions.push(
            combineLatest([
                this.userPreferencesService.getUserAppPreferences().pipe(
                    map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors.generalColors.color1),
                    catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color1))
                ),
                this.data
            ]).subscribe(([chartColor, data]) => {
                this.zone.runOutsideAngular(() => {
                    this.loading = data === null;
                    this.cdr.detectChanges();
                    if (this.loading) {
                        return;
                    }

                    if (!this.chart) {
                        this.chart = this.initChart(chartColor);
                    }

                    this.setScale(data.scale);

                    this.chart.data = data.items;

                    // Add a guide
                    const value = this.calculateAverage(this.chart.data, 'value');
                    // this check prevents an "EventDispatcher is disposed" error
                    if (this.averageRange.value !== value) {
                        this.averageRange.value = value;
                    }
                    this.averageRange.grid.stroke = am4core.color('#396478');
                    this.averageRange.grid.strokeWidth = 1;
                    this.averageRange.grid.strokeOpacity = 1;
                    this.averageRange.grid.strokeDasharray = '3,3';
                    this.averageRange.axisFill.tooltip = new am4core.Tooltip();
                    this.averageRange.axisFill.tooltipText = `Average: ${value.toFixed(0)}`;
                    this.averageRange.axisFill.interactionsEnabled = true;
                    this.averageRange.axisFill.isMeasured = true;
                    this.averageRange.axisFill.tooltip.getFillFromObject = false;
                    this.averageRange.axisFill.tooltip.background.fill = am4core.color('#396478');
                });
            })
        );
    }

    ngOnDestroy() {
        this.zone.runOutsideAngular(() => {
            if (this.chart) {
                this.chart.dispose();
            }
        });

        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
